import { currentLocale } from '@/lib/locale';
import { useState, useMemo } from 'react';
import { CreditCard, SlidersHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { OverviewTab } from '@/components/subscriptions/OverviewTab';
import { GeneralSubscriptionsTab } from '@/components/subscriptions/GeneralSubscriptionsTab';
import { AIToolsTab } from '@/components/subscriptions/AIToolsTab';
import { TransactionsTab } from '@/components/subscriptions/TransactionsTab';
import { AreaDrilldownTab } from '@/components/subscriptions/AreaDrilldownTab';
import { HeadcountAnalysisTab } from '@/components/subscriptions/HeadcountAnalysisTab';
import { AdvisorTab } from '@/components/subscriptions/AdvisorTab';
import { useAreaBreakdown, useVendorRanking, useExecutiveSummary } from '@/hooks/useSubscriptions';
import type { GlobalSubscriptionFilters } from '../../../../shared/types';

export function SubscriptionsIntelligencePage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<GlobalSubscriptionFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');

  // Fetch options for selects (unfiltered)
  const { data: areaRows = [] } = useAreaBreakdown({});
  const { data: vendors = [] } = useVendorRanking();
  const { data: summary = [] } = useExecutiveSummary();

  const uniqueDepartments = useMemo(
    () => [...new Set(areaRows.map((r) => r.NetSuite_Departamentos))].filter(Boolean).sort(),
    [areaRows]
  );
  const uniqueVendors = useMemo(
    () => vendors.map((v) => v.Normalized_Vendor).filter(Boolean).sort(),
    [vendors]
  );
  const availableMonths = useMemo(
    () => [...summary].map((r) => r.Month).sort().reverse(),
    [summary]
  );

  const fmtMonthLabel = (m: string) => {
    const [y, mo] = m.split('-');
    return new Date(+y, +mo - 1).toLocaleString(currentLocale(), { month: 'long', year: 'numeric' });
  };

  const handleMonthSelect = (month: string) => {
    setSelectedMonth(month);
    if (month === 'all') {
      setFilters((f) => ({ ...f, dateFrom: undefined, dateTo: undefined }));
    } else {
      setFilters((f) => ({ ...f, dateFrom: month, dateTo: month }));
    }
  };

  const activeFilterCount = [
    filters.dateFrom || filters.dateTo,   // date range counts as 1
    filters.department,
    filters.vendor,
    filters.isAITool !== undefined ? true : undefined,
  ].filter(Boolean).length;

  const clearFilters = () => { setFilters({}); setSelectedMonth('all'); };

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 shrink-0 mt-0.5">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('subscriptions.page_title')}</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{t('subscriptions.page_subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={selectedMonth} onValueChange={handleMonthSelect}>
            <SelectTrigger className="h-8 text-sm w-44">
              <SelectValue placeholder={t('subscriptions.filters.all_months', 'Todos los meses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('subscriptions.filters.all_months', 'Todos los meses')}</SelectItem>
              {availableMonths.map((m) => (
                <SelectItem key={m} value={m}>{fmtMonthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-2"
            onClick={() => setShowFilters((v) => !v)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {t('subscriptions.filters.title', 'Filtros')}
            {activeFilterCount > 0 && (
              <Badge className="h-4 w-4 p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground rounded-full">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Global filter bar */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.filters.date_from', 'Desde')}</label>
              <input
                type="month"
                value={filters.dateFrom ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || undefined }))}
                className="h-8 text-sm px-2 rounded-md border bg-background text-foreground w-36"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.filters.date_to', 'Hasta')}</label>
              <input
                type="month"
                value={filters.dateTo ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || undefined }))}
                className="h-8 text-sm px-2 rounded-md border bg-background text-foreground w-36"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.filters.department', 'Área / Depto.')}</label>
              <Select
                value={filters.department ?? 'all'}
                onValueChange={(v) => setFilters((f) => ({ ...f, department: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-8 text-sm w-52">
                  <SelectValue placeholder={t('subscriptions.filters.all_depts', 'Todos los departamentos')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('subscriptions.filters.all_depts', 'Todos')}</SelectItem>
                  {uniqueDepartments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.filters.vendor', 'Vendor / Tool')}</label>
              <Select
                value={filters.vendor ?? 'all'}
                onValueChange={(v) => setFilters((f) => ({ ...f, vendor: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-8 text-sm w-48">
                  <SelectValue placeholder={t('subscriptions.filters.all_vendors', 'Todos los vendors')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('subscriptions.filters.all_vendors', 'Todos')}</SelectItem>
                  {uniqueVendors.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.filters.type', 'Tipo')}</label>
              <Select
                value={filters.isAITool === undefined ? 'all' : filters.isAITool ? 'ai' : 'non-ai'}
                onValueChange={(v) => setFilters((f) => ({
                  ...f,
                  isAITool: v === 'all' ? undefined : v === 'ai',
                }))}
              >
                <SelectTrigger className="h-8 text-sm w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('subscriptions.filters.all_types', 'Todos')}</SelectItem>
                  <SelectItem value="ai">{t('subscriptions.filters.ai_only', 'Solo AI')}</SelectItem>
                  <SelectItem value="non-ai">{t('subscriptions.filters.non_ai', 'No-AI')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-muted-foreground" onClick={clearFilters}>
                <X className="h-3 w-3" />
                {t('subscriptions.filters.clear', 'Limpiar')}
              </Button>
            )}
          </div>
          {activeFilterCount > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {t('subscriptions.filters.note', 'Los filtros aplican a los tabs que soportan cada dimensión. Overview usa todos los datos históricos.')}
            </p>
          )}
        </Card>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">{t('subscriptions.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="subscriptions">{t('subscriptions.tabs.general')}</TabsTrigger>
          <TabsTrigger value="ai-tools">{t('subscriptions.tabs.ai_tools')}</TabsTrigger>
          <TabsTrigger value="transactions">{t('subscriptions.tabs.transactions')}</TabsTrigger>
          <TabsTrigger value="area">{t('subscriptions.tabs.area')}</TabsTrigger>
          <TabsTrigger value="headcount">{t('subscriptions.tabs.headcount')}</TabsTrigger>
          <TabsTrigger value="advisor">{t('ia-native.subscriptionsintelligence.advisor')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="subscriptions">
          <GeneralSubscriptionsTab globalFilters={filters} />
        </TabsContent>
        <TabsContent value="ai-tools">
          <AIToolsTab globalFilters={filters} />
        </TabsContent>
        <TabsContent value="transactions">
          <TransactionsTab globalFilters={filters} />
        </TabsContent>
        <TabsContent value="area">
          <AreaDrilldownTab globalFilters={filters} />
        </TabsContent>
        <TabsContent value="headcount">
          <HeadcountAnalysisTab globalFilters={filters} />
        </TabsContent>
        <TabsContent value="advisor">
          <AdvisorTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
